import type { BehaviorPropertySnapshotSource } from '~/composables/useBehaviorEventTracking/types';

import { useLocalStorage } from '@vueuse/core';

const OFFLINE_FAVORITES_KEY = 'shorts:offlineFavorites';

export interface OfflineFavoriteEntry {
	propertyId: string;
	/** Commercial id used for behavior events; may be missing on legacy entries. */
	commercialId?: string;
	/**
	 * Snapshot source captured at favorite time. Login may happen on another
	 * property or another page, so the event cannot use the screen that is
	 * visible when the buffer flushes.
	 */
	property?: BehaviorPropertySnapshotSource;
}

interface OfflineFavoritesData {
	/** Legacy shape — migrated to `entries` on read. */
	ids?: string[];
	entries?: OfflineFavoriteEntry[];
}

const DEFAULT_DATA: OfflineFavoritesData = { entries: [] };

function normalizeEntries(data: OfflineFavoritesData): OfflineFavoriteEntry[] {
	if (data.entries?.length) return data.entries;
	if (data.ids?.length) {
		return data.ids.map(propertyId => ({ propertyId }));
	}
	return [];
}

function cloneSnapshotSource(
	property: BehaviorPropertySnapshotSource
): BehaviorPropertySnapshotSource {
	return {
		region: property.region,
		propertyType: property.propertyType
			? {
					identifier: property.propertyType.identifier,
					name: property.propertyType.name
				}
			: null,
		askingPrice: property.askingPrice,
		rentPrice: property.rentPrice ?? null,
		area: property.area ?? null,
		bedrooms: property.bedrooms ?? null,
		ad: property.ad?.transactionType
			? { transactionType: property.ad.transactionType }
			: null
	};
}

export function hasOfflineFavorites(): boolean {
	if (!import.meta.client) return false;
	try {
		const stored = localStorage.getItem(OFFLINE_FAVORITES_KEY);
		if (!stored) return false;
		const data = JSON.parse(stored) as OfflineFavoritesData;
		return normalizeEntries(data).length > 0;
	} catch {
		// localStorage blocked or invalid JSON: treat as an empty buffer
		return false;
	}
}

export function useOfflineFavorites() {
	const data = useLocalStorage<OfflineFavoritesData>(
		OFFLINE_FAVORITES_KEY,
		DEFAULT_DATA
	);

	function getEntries(): OfflineFavoriteEntry[] {
		return normalizeEntries(data.value).map(entry => ({ ...entry }));
	}

	function writeEntries(entries: OfflineFavoriteEntry[]): void {
		data.value = { entries };
	}

	function isOfflineFavorite(propertyId: string): boolean {
		return normalizeEntries(data.value).some(
			entry => entry.propertyId === propertyId
		);
	}

	function addOfflineFavorite(
		propertyId: string,
		commercialId?: string | null,
		property?: BehaviorPropertySnapshotSource | null
	): void {
		const entries = normalizeEntries(data.value);
		if (entries.some(entry => entry.propertyId === propertyId)) return;

		const trimmedCommercialId = commercialId?.trim();
		writeEntries([
			...entries,
			{
				propertyId,
				...(trimmedCommercialId ? { commercialId: trimmedCommercialId } : {}),
				...(property ? { property: cloneSnapshotSource(property) } : {})
			}
		]);
	}

	function removeOfflineFavorite(propertyId: string): void {
		writeEntries(
			normalizeEntries(data.value).filter(
				entry => entry.propertyId !== propertyId
			)
		);
	}

	function getIds(): string[] {
		return getEntries().map(entry => entry.propertyId);
	}

	function clearAll(): void {
		writeEntries([]);
	}

	return {
		isOfflineFavorite,
		addOfflineFavorite,
		removeOfflineFavorite,
		getEntries,
		getIds,
		clearAll
	};
}
